import { chain, sortBy, sumBy, union } from 'lodash'
import moment from 'moment'

export const occupationPeriod = {
  day: 'DAY',
  month: 'MONTH'
}

export const dateFormat = {
  byDay: 'DD MMM',
  byMonth: 'MMM YYYY'
}

export const colors = {
  grey: '#EFEFEF',
  white: '#FFFFFF'
}

export const occupancyDataKeySuffix = {
  workingHours: '-working-hours',
  nonWorkingHours: '-non-working-hours'
}

/**
 * Transforms loading station occupation data received from the backend into
 * a suitable format for the recharts library.
 *
 * @param dateDataKey string property containing occupation data for each date
 * @param inputData[] data received from backend
 */
export function transformOccupationData(inputData = [], dateDataKey) {
  const occupationData = inputData.map(entry => ({
    date: moment(entry.date).format(dateFormat.byMonth),
    ...entry[dateDataKey].reduce(
      (acc, val) => ({
        ...acc,
        [val.name]: Math.ceil(val.occupation),
        [`${val.name}${occupancyDataKeySuffix.workingHours}`]: Math.ceil(val.occupationWorkingHours),
        [`${val.name}${occupancyDataKeySuffix.nonWorkingHours}`]: Math.ceil(val.occupationNonWorkingHours)
      }),
      {}
    )
  }))

  return {
    data: occupationData,
    stations: union(...inputData.map(entry => entry[dateDataKey].map(type => type.name))).sort()
  }
}

/**
 * Derives colors for products and loading stations from the terminal config
 * @param {Quay[]} quays Quays in terminal
 */
export function getQuayConfig(quays) {
  const productColors = {}
  const stationColors = {}
  const productLoadSpeeds = {}

  quays.forEach(({ jetties, mobrus }) => {
    jetties.forEach(({ name, color, products }) => {
      stationColors[name] = color
      products.forEach(({ product, color, loadSpeed }) => {
        productColors[product] = color
        productLoadSpeeds[product] = loadSpeed
      })
    })

    mobrus.forEach(({ name, color, products }) => {
      stationColors[name] = color
      products.forEach(({ product, color, loadSpeed }) => {
        productColors[product] = color
        productLoadSpeeds[product] = loadSpeed
      })
    })
  })

  return { stationColors, productColors, productLoadSpeeds }
}

/**
 * Formats order adherence KPI data received from backend
 * @param {OrderAdherence[]} input adherence data
 * @returns {{data: *, delayList: *}} formatted adherence data
 */
export const formatPlanningAdherenceData = input => {
  const formattedInput = input.map(({ difference, orders, byDelay }) => ({
    // granularity is always HOUR
    difference,
    // chart uses 'total'
    total: orders,
    ...byDelay.reduce((acc, val) => {
      acc[val.name] = val.orders
      return acc
    }, {})
  }))

  return {
    data: sortBy(formattedInput, 'difference'),
    // get unique delays from adherence data
    delayList: union(...input.map(adherence => adherence.byDelay.map(type => type.name)))
  }
}

export const percentageTickFormatter = tickItem => {
  return `${tickItem}%`
}

export const processLaybyOccupationData = inputData => {
  return chain(inputData)
    .values()
    .flatten()
    .groupBy('date')
    .entries()
    .value()
    .map(([, valuesArray]) => ({
      // _.entries() converts grouped date key to a string, so we take the date property from an entry in valuesArray instead
      date: moment(valuesArray[0].date).format(dateFormat.byMonth),
      yara: moment.duration(sumBy(valuesArray, 'yara')).asHours(),
      nonYara: moment.duration(sumBy(valuesArray, 'nonYara')).asHours()
    }))
}
